<div class="flex flex-col gap-y-15">
  <div class="flex flex-col gap-y-5">
    <p class="text-sm text-gray">
      Enter any cash to set aside. Set-aside cash is included in the advisor-directed Custom Holdings sleeve
      and will be protected and excluded from investment until the advisor requests otherwise.
    </p>

    <!-- The sweep control, defined ONCE and placed in two states below.

         ⚠️ A template rather than two copies. It moves inside the error box when there is an
         error, and stands alone when there is not -- and a button whose label, colour, tooltip
         and accessible name are all derived would have been four things to keep in step across
         two copies. `ngTemplateOutlet` keeps it one control that appears in two places.

         The same sweep the allocation step offers, pointed at cash, on the shared `.button`
         classes. Colour tracks the Investable box above -- mint while money is to place, alert
         when more is committed than the account holds, which is also the only state that puts
         it inside the red box.

         ⚠️ Same shape as the Allocate to Models control: static label, amount on the icon. See
         `remainingLabel` there and here -- the two are meant to read alike, because they are the
         same gesture on the same outstanding figure. -->
    <ng-template #sweepControl>
      <button type="button" class="button is-sm flex-shrink-0"
        [class.is-mint]="!isOverAllocated()"
        [class.is-alert]="isOverAllocated()"
        [attr.aria-label]="remainingHint()"
        (click)="store.applyRemainingToCash()">
        {{ remainingLabel() }}
      </button>
      <!-- `aria-hidden`, because the button already carries this sentence as its accessible
           name. Announcing it twice would read the amount out at every focus stop. -->
      <i class="material-icons cursor-help text-gray-dark flex-shrink-0" style="font-size: 1.8rem"
        [title]="remainingHint()" aria-hidden="true">info_outline</i>
    </ng-template>

    <!-- ⚠️ ABOVE the input, not below it (AndyP, 2026-09-15). The message sat under the cash row
         while the control that fixes it floated above -- so the advisor read the problem, then
         looked back up past the box to find the remedy. Problem and remedy are one block now, and
         they sit where the eye lands before the input rather than after it.

         One place, one message: the rule and its wording live together in
         `protectedCashStepBlockedReason`, which is also what disables Next, so what the advisor
         reads and what blocks them cannot drift apart.

         ⚠️ THE SAME BANNER as step 1 and the buy step, down to the classes. It was bare red text
         with a warning glyph, which made one condition look like three different kinds of problem
         depending on which step the advisor was standing on.

         🟢 `role="alert"` is kept. The styling changed, not the fact that this is announced. -->
    @if (store.protectedCashStepBlockedReason(); as reason) {
      <div class="flex flex-wrap items-center justify-between px-15 py-10 text-sm border rounded gap-x-15 gap-y-5 border-red-light bg-red-lightest text-red-dark"
        role="alert">
        <span>{{ reason }}</span>
        <!-- ⚠️ Still gated on `canApplyRemaining`. Being over-committed does not guarantee this
             step can fix it: when the overspend came from buys or models and nothing has been set
             aside here, there is nothing to return and the button would do nothing. The message
             names the other places for exactly that case. -->
        @if (canApplyRemaining()) {
          <span class="flex items-center flex-shrink-0 gap-x-5">
            <ng-container *ngTemplateOutlet="sweepControl" />
          </span>
        }
      </div>
    } @else if (canApplyRemaining()) {
      <div class="flex items-center justify-end mb-10 gap-x-5">
        <ng-container *ngTemplateOutlet="sweepControl" />
      </div>
    }

    <!-- ONE INPUT AND NOTHING ELSE, deliberately, as of 2026-09-15.

         This step briefly itemised the other two kinds of held cash that reach it -- buys whose
         ticker is unresolved, and sales whose proceeds are held -- first as a flat list, then as
         named sections with subtotals, then as a single Total Cash On Hold in the foot. All of it
         is gone. The step tracks ONE thing: the amount the advisor chooses to set aside.

         ⚠️ Do not put a total back without a reason that survives this: `totalProtectedCash` is
         `protectedCashFromReview + reviewSoldValue + cashInput`, and only the last is decided
         here -- so a foot on this screen necessarily showed a bigger number than the box above it
         with nothing nearby to explain the gap. The step is not where a request is reported.

         🟢 Nothing is hidden by removing it. The bar's Cash On Hold run carries exactly that
         figure and is on screen throughout, and Review & Submit plus both request trackers
         itemise every line that makes it up. -->
    <div class="w-full overflow-x-auto">
      <table class="w-full text-sm border-collapse">
        <tbody>
          <!-- `bg-mint-lightest` is the same tint step 2 puts behind its editable columns and the
               cash band uses on Review & Submit: mint means the request throughout this wizard,
               and specifically the part of it being decided. The left rule is the same device the
               cash band uses to bind a group together. Both are kept now that this is the only
               row, so the input still reads as the thing being decided rather than as a lone
               table row above a total. -->
          <tr class="border-b border-gray-lightest bg-mint-lightest">
            <td class="py-8 pl-15 pr-8 align-middle border-l-4 border-mint-light">
              <!-- Uppercased in CSS rather than typed in capitals: a screen reader says "amount"
                   rather than spelling it out, and the string stays readable in the source.
                   No sub-label: what set-aside cash MEANS is said once, in the paragraph at the
                   top of the step. Repeating it here put two explanations on a screen with one
                   input, which is where the advisor is least in need of prose. -->
              <span class="font-semibold tracking-wide uppercase text-mint-darkest">Amount of cash to set aside</span>
            </td>
            <td class="py-8 pl-8 pr-15 text-right align-middle">
              <!-- The $ sits outside the box: a number input cannot carry a prefix, and typing one
                   would make the value non-numeric. Same treatment as the buy step's amount. -->
              <div class="flex items-center justify-end gap-x-5">
                <span class="text-gray-darkest">$</span>
                <input #cashInput type="number" min="0" [formControl]="store.cashControl"
                  appSelectOnFocus appCommitOnEnter appTwoDecimals
                  class="w-120 text-right input is-sm no-spinner" aria-label="Amount of cash to set aside" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>
